import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Drawer,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { streamClaudeChat, sendClaudeChat } from '../api/claudeApi';

// ---- static config ---------------------------------------------------------

const CHECKLIST = [
  { key: 'business', label: 'Business Loaded' },
  { key: 'financials', label: 'Financial Statements' },
  { key: 'recommendation', label: 'Credit Recommendation' },
  { key: 'risk', label: 'Risk Signals' },
  { key: 'monitoring', label: 'Monitoring Events' },
  { key: 'external', label: 'External Intelligence', note: 'on request' },
  { key: 'documents', label: 'Documents' },
];

const SUGGESTED_QUESTIONS = [
  'Summarize Business',
  'Explain Credit Recommendation',
  'Why was this company declined?',
  'What changed since last month?',
  'Should I increase the limit?',
  'Biggest Risk Factors',
  'Explain the PD',
  'Explain LGD',
  'Draft Credit Memo',
  'Prepare Committee Summary',
  'Recommend Insurance Structure',
  'Would you fund this company?',
  'Compare with Industry',
  'Show Negative Signals',
  'Show Positive Signals',
];

const ACTIONS = [
  { label: 'Increase Credit Limit', prompt: 'Should CrediArc increase the credit limit for this business? Give your recommendation with the CrediArc drivers.' },
  { label: 'Reduce Credit Limit', prompt: 'Should CrediArc reduce the credit limit for this business? Give your recommendation with the CrediArc drivers.' },
  { label: 'Create Monitoring Alert', prompt: 'What monitoring alert should we create for this business, and what should it trigger on? Base it on CrediArc signals.' },
  { label: 'Draft Memo', prompt: 'Draft a concise underwriting credit memo for this business based on CrediArc\'s assessment.' },
  { label: 'Generate PDF', prompt: 'Prepare a committee-ready summary of CrediArc\'s recommendation that I can export to PDF.' },
  { label: 'Export Recommendation', prompt: 'Summarize CrediArc\'s current recommendation in an exportable format: recommended limit, confidence, primary drivers, and decision.' },
  { label: 'Request Updated Financials', prompt: 'Draft a request to the business for updated financial statements, noting which periods CrediArc still needs.' },
  { label: 'Flag For Manual Review', prompt: 'Explain whether this business should be flagged for manual review, and why, based on CrediArc signals.' },
];

const KICKOFF_PROMPT =
  "Provide CrediArc's underwriting summary for this business: the recommendation, the key drivers, and the biggest risk factors — using only CrediArc's proprietary data. Do not search the web.";

// ---- markdown styling ------------------------------------------------------

const markdownSx = {
  fontSize: 13,
  lineHeight: 1.6,
  color: '#1f2a44',
  '& p': { m: 0, mb: 1 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { m: 0, mb: 1, pl: 2.5 },
  '& li': { mb: 0.25 },
  '& h1, & h2, & h3, & h4': { m: 0, mb: 0.75, mt: 1, fontSize: 14, fontWeight: 700, color: '#1f2a44' },
  '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': { mt: 0 },
  '& a': { color: '#2f57c4', textDecoration: 'underline' },
  '& strong': { color: '#1f2a44' },
  '& code': { background: '#e9ecf3', borderRadius: '4px', px: 0.5, py: '1px', fontSize: 12, fontFamily: 'monospace' },
  '& pre': { background: '#e9ecf3', borderRadius: '6px', p: 1, overflowX: 'auto', '& code': { background: 'none', p: 0 } },
  '& table': { borderCollapse: 'collapse', width: '100%', fontSize: 12, my: 1 },
  '& th, & td': { border: '0.5px solid #d5d9e4', px: 0.75, py: 0.4, textAlign: 'left' },
  '& th': { background: '#f0f2f7', fontWeight: 700 },
  '& blockquote': { m: 0, mb: 1, pl: 1.25, py: 0.5, borderLeft: '3px solid #e0a458', background: '#fdf6ec', color: '#7a5a1e', fontSize: 12 },
};

// ---- helpers ---------------------------------------------------------------

const MessageMarkdown = ({ text }) => (
  <Box sx={markdownSx}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
      }}
    >
      {text || ''}
    </ReactMarkdown>
  </Box>
);

// Compute which preload categories the loaded context actually contains.
const computeChecklist = (entity) => {
  const blob = JSON.stringify(entity || {}).toLowerCase();
  const has = (v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
  return {
    business: has(entity?.businessProfile) || has(entity?.executiveSummary) || has(entity?.summaryData),
    financials: has(entity?.financialInfo),
    recommendation: has(entity?.underwritingScores) || blob.includes('recommendedlimit') || blob.includes('recommended limit'),
    risk: /risk|delinqu|failure|altman|arrear|paydex/.test(blob),
    monitoring: has(entity?.monitoringEvents),
    external: true, // capability — available on request
    documents: /document|upload|attachment|"file"/.test(blob),
  };
};

// ---- component -------------------------------------------------------------

const UnderwritingCopilot = ({ tenantName, entityName, entity, embedded = false }) => {
  const openKey = `crediarc:copilot:open:${entityName || 'entity'}`;
  const historyKey = `crediarc:copilot:history:${entityName || 'entity'}`;

  const restoredOpen = (() => {
    try { return sessionStorage.getItem(openKey) === '1'; } catch (e) { return false; }
  })();
  const restoredMessages = (() => {
    try { return JSON.parse(localStorage.getItem(historyKey) || 'null') || []; } catch (e) { return []; }
  })();

  const [open, setOpen] = useState(restoredOpen);
  const [messages, setMessages] = useState(restoredMessages); // [{ role, content, hidden?, streaming? }]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const startedRef = useRef(restoredMessages.length > 0);

  const checklist = computeChecklist(entity);

  // Persist open state (until browser close) and finalized history (durable, per business).
  useEffect(() => {
    try { sessionStorage.setItem(openKey, open ? '1' : '0'); } catch (e) { /* ignore */ }
  }, [openKey, open]);
  useEffect(() => {
    try {
      const persistable = messages.filter((m) => !m.streaming);
      localStorage.setItem(historyKey, JSON.stringify(persistable));
    } catch (e) { /* ignore */ }
  }, [historyKey, messages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Send a turn. allowWeb=false → CrediArc-only; true → web permitted if needed.
  const send = useCallback(
    async (text, { allowWeb = true, hidden = false } = {}) => {
      const clean = String(text || '').trim();
      if (!clean || loading) return;

      const base = [...messages.filter((m) => !m.streaming), { role: 'user', content: clean, hidden }];
      // Show user turn (unless hidden) + a streaming assistant placeholder.
      setMessages([...base, { role: 'assistant', content: '', streaming: true }]);
      setLoading(true);
      setError(null);

      const payloadMessages = base.map(({ role, content }) => ({ role, content }));
      const updateAssistant = (content, streaming) =>
        setMessages([...base, { role: 'assistant', content, streaming }]);

      try {
        const { reply } = await streamClaudeChat(
          { tenantName, entityName, entity, messages: payloadMessages, allowWeb },
          { onDelta: (full) => updateAssistant(full, true) }
        );
        updateAssistant(reply, false);
      } catch (streamErr) {
        // Fall back to the non-streaming endpoint before surfacing an error.
        try {
          const res = await sendClaudeChat({ tenantName, entityName, entity, messages: payloadMessages, allowWeb });
          updateAssistant(res?.data?.reply || '(no response)', false);
        } catch (e) {
          const status = e?.response?.status || streamErr?.status;
          const detail = e?.response?.data?.message || streamErr?.message;
          setMessages(base); // drop the empty placeholder
          setError(
            status
              ? `Copilot request failed (${status})${detail ? `: ${detail}` : ''}.`
              : 'The Copilot is unavailable right now. Please try again.'
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [messages, loading, tenantName, entityName, entity]
  );

  const handleOpen = () => {
    setOpen(true);
    if (!startedRef.current) {
      startedRef.current = true;
      send(KICKOFF_PROMPT, { allowWeb: false, hidden: true });
    }
  };

  // Embedded mode starts EMPTY — no auto-kickoff. Re-running the AI Review must NOT trigger
  // the Copilot to re-analyze; it stays idle (suggested prompts + input) until the user asks.
  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    send(text, { allowWeb: true });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const visibleMessages = messages.filter((m) => !m.hidden);
  const conversationStarted = messages.some((m) => m.role === 'assistant' && (m.content || m.streaming));

  // -- launcher (inline card in the page) --
  const launcher = (
    <Box sx={{ px: '25px', my: 2 }}>
      <Paper
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleOpen()}
        elevation={0}
        sx={{
          cursor: 'pointer',
          border: '1.5px solid #2f57c4',
          borderRadius: '12px',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          transition: 'background 0.15s',
          '&:hover': { background: '#f3f6ff' },
        }}
      >
        <Box sx={{ flexShrink: 0, width: 42, height: 42, borderRadius: '50%', background: '#e8efff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AutoAwesomeIcon sx={{ color: '#2f57c4' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, color: '#1f2a44' }}>Underwriting Copilot</Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: '#5f6b85' }}>
            Powered by CrediArc Intelligence — ask about the decision, risk, financials, or draft a memo.
          </Typography>
        </Box>
        <ArrowForwardIcon sx={{ color: '#9aa3b8' }} />
      </Paper>
    </Box>
  );

  const panel = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e3e6ee', background: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesomeIcon sx={{ color: '#2f57c4' }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#1f2a44', fontSize: 15, lineHeight: 1.2 }}>
                    Underwriting Copilot
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#7a8398' }}>Powered by CrediArc Intelligence</Typography>
                </Box>
              </Box>
              {!embedded && (
                <IconButton size="small" aria-label="Close" onClick={() => setOpen(false)}>
                  <CloseIcon sx={{ fontSize: 18, color: '#9aa3b8' }} />
                </IconButton>
              )}
            </Box>
            {entityName && (
              <Typography sx={{ mt: 0.75, fontSize: 12, color: '#5f6b85' }}>
                Assessing <strong style={{ color: '#1f2a44' }}>{entityName}</strong>
              </Typography>
            )}
          </Box>

          {/* Preload checklist */}
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #eef0f5', background: '#f6f8fc' }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#7a8398', letterSpacing: 0.4, textTransform: 'uppercase', mb: 0.75 }}>
              Loaded for this business
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1.5, rowGap: 0.5 }}>
              {CHECKLIST.map((item) => {
                const on = checklist[item.key];
                return (
                  <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <CheckCircleIcon sx={{ fontSize: 14, color: on ? '#1f9d55' : '#c7cedd' }} />
                    <Typography sx={{ fontSize: 11.5, color: on ? '#3a4358' : '#a2aabb' }}>
                      {item.label}
                      {item.note && on ? <span style={{ color: '#9aa3b8' }}> · {item.note}</span> : null}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Conversation */}
          <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {visibleMessages.map((m, i) => (
              <Box
                key={i}
                sx={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: m.role === 'user' ? '85%' : '95%',
                  background: m.role === 'user' ? '#e8efff' : '#fff',
                  border: m.role === 'user' ? 'none' : '1px solid #e6e9f0',
                  borderRadius: '12px',
                  borderTopRightRadius: m.role === 'user' ? '4px' : '12px',
                  borderTopLeftRadius: m.role === 'user' ? '12px' : '4px',
                  px: 1.75,
                  py: 1.25,
                }}
              >
                {m.role === 'user' ? (
                  <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: '#2f57c4', whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
                ) : m.content ? (
                  <MessageMarkdown text={m.content} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={13} sx={{ color: '#2f57c4' }} />
                    <Typography sx={{ fontSize: 12, color: '#7a8398' }}>Consulting CrediArc's assessment…</Typography>
                  </Box>
                )}
              </Box>
            ))}

            {/* Actions after the latest completed assistant answer */}
            {conversationStarted && !loading && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.5 }}>
                {ACTIONS.map((a) => (
                  <Chip
                    key={a.label}
                    label={a.label}
                    size="small"
                    onClick={() => send(a.prompt, { allowWeb: false })}
                    sx={{ fontSize: 11, height: 26, borderRadius: '8px', background: '#eef2fb', color: '#2f57c4', '&:hover': { background: '#e0e8fb' } }}
                  />
                ))}
              </Box>
            )}

            {error && (
              <Typography sx={{ alignSelf: 'flex-start', fontSize: 12, color: '#c0392b' }}>{error}</Typography>
            )}
          </Box>

          {/* Suggested questions */}
          <Box sx={{ px: 2, pt: 1, pb: 0.5, borderTop: '1px solid #eef0f5', background: '#fff' }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#7a8398', letterSpacing: 0.4, textTransform: 'uppercase', mb: 0.5 }}>
              Suggested
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.6, overflowX: 'auto', pb: 0.75, '&::-webkit-scrollbar': { height: 4 } }}>
              {SUGGESTED_QUESTIONS.map((q) => (
                <Chip
                  key={q}
                  label={q}
                  size="small"
                  onClick={() => send(q, { allowWeb: false })}
                  sx={{ flexShrink: 0, fontSize: 11.5, height: 27, borderRadius: '8px', border: '1px solid #dfe4ee', background: '#fff', color: '#3a4358', '&:hover': { background: '#f3f6ff', borderColor: '#2f57c4' } }}
                />
              ))}
            </Box>
          </Box>

          {/* Input */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.5, borderTop: '1px solid #eef0f5', background: '#fff' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask the Copilot about this business…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <IconButton
              aria-label="Send"
              onClick={handleSend}
              disabled={loading || !input.trim()}
              sx={{ background: '#2f57c4', color: '#fff', '&:hover': { background: '#264aa8' }, '&.Mui-disabled': { background: '#c7cedd', color: '#fff' } }}
            >
              {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ArrowUpwardIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        </Box>
  );

  // Embedded mode: render the chat panel inline (used inside the AI Assessment Review
  // window), no launcher and no drawer.
  if (embedded) {
    return (
      <Box sx={{ height: 520, border: '1px solid #e3e6ee', borderRadius: '12px', overflow: 'hidden', background: '#fbfcfe' }}>
        {panel}
      </Box>
    );
  }

  return (
    <>
      {launcher}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, background: '#fbfcfe' } }}
      >
        {panel}
      </Drawer>
    </>
  );
};

export default UnderwritingCopilot;
